import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-(--color-primary) text-(--color-primary-foreground) hover:bg-(--color-primary)/90',
        outline: 'border border-(--color-border) bg-(--color-background) hover:bg-(--color-muted)',
        ghost: 'hover:bg-(--color-muted)',
        link: 'underline-offset-4 hover:underline text-(--color-primary)',
        destructive: 'bg-(--color-destructive) text-(--color-destructive-foreground) hover:bg-(--color-destructive)/90',
        secondary: 'bg-(--color-secondary) text-(--color-secondary-foreground) hover:bg-(--color-secondary)/80',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-6',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';
